import { Dialog } from "@mui/material";
import { styled } from "@mui/material/styles";
import { 
  X, 
  Gamepad2, 
  User, 
  Calendar, 
  Clock, 
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  Hash,
  Mail,
  Phone,
  Award
} from "lucide-react";
import { toast } from "react-toastify";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(6px)",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
  },
  "& .MuiPaper-root": {
    maxWidth: "600px",
    width: "100%",
    border: `1px solid ${theme.palette.mode === "dark" ? "#444" : "#9333ea"}`,
    borderRadius: 16,
    padding: theme.spacing(3),
    backgroundColor:
      theme.palette.mode === "dark"
        ? "#111827"
        : theme.palette.background.paper,
  },
}));

export default function ViewGameOrderModal({ 
  open, 
  onClose, 
  order,
  onComplete,
  onReject,
  isCompleting,
  isRejecting
}) {
  if (!order) return null;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      succeeded: { bg: "bg-green-100", text: "text-green-800", icon: CheckCircle, label: "Completed" },
      pending: { bg: "bg-yellow-100", text: "text-yellow-800", icon: Clock, label: "Pending" },
      processing: { bg: "bg-blue-100", text: "text-blue-800", icon: Clock, label: "Processing" },
      failed: { bg: "bg-red-100", text: "text-red-800", icon: XCircle, label: "Failed" },
      cancelled: { bg: "bg-gray-100", text: "text-gray-800", icon: AlertCircle, label: "Cancelled" }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <div className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${config.bg} ${config.text}`}>
        <Icon size={14} />
        {config.label}
      </div>
    );
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <div className="w-full">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
              <Gamepad2 size={24} className="text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Game Order Details</h2>
              <p className="text-sm text-gray-500">View complete order information</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mb-6">
          {getStatusBadge(order.status)}
        </div>

        <div className="space-y-4 mb-6">
          <h3 className="font-semibold text-gray-800 border-b pb-2">Order Information</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <Hash size={14} />
                Transaction ID
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{order.txnNumber}</span>
                <button
                  onClick={() => copyToClipboard(order.txnNumber)}
                  className="text-gray-400 hover:text-blue-600"
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <Calendar size={14} />
                Date Created
              </div>
              <div className="font-medium">
                {new Date(order.createdAt).toLocaleString()}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <User size={14} />
                Agent
              </div>
              <div className="font-medium">
                {order.agent?.username || order.user?.username || 'N/A'}
              </div>
              {order.agent?.email && (
                <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                  <Mail size={12} />
                  {order.agent.email}
                </div>
              )}
            </div>

            {order.source === 'stripe_card' && order.customerDetails && (
              <div>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                  <User size={14} />
                  Customer
                </div>
                <div className="font-medium">
                  {order.customerDetails.fullName || order.customerDetails.email}
                </div>
                {order.customerDetails.phoneNumber && (
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    <Phone size={12} />
                    {order.customerDetails.phoneNumber}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <h3 className="font-semibold text-gray-800 border-b pb-2">Game Details</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500 mb-1">Product</div>
              <div className="font-medium">
                {order.productDetails?.productName?.en || order.productDetails?.productName || 'N/A'}
              </div>
              {order.productDetails?.description && (
                <div className="text-xs text-gray-500 mt-1">
                  {order.productDetails.description}
                </div>
              )}
            </div>

            <div>
              <div className="text-sm text-gray-500 mb-1">Game Account</div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{order.receiver}</span>
                <button
                  onClick={() => copyToClipboard(order.receiver)}
                  className="text-gray-400 hover:text-blue-600"
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>

            {order.operator && (
              <div>
                <div className="text-sm text-gray-500 mb-1">Game</div>
                <div className="font-medium capitalize">{order.operator}</div>
              </div>
            )}

            {order.productDetails?.gameType && (
              <div>
                <div className="text-sm text-gray-500 mb-1">Game Type</div>
                <div className="font-medium capitalize">{order.productDetails.gameType}</div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <h3 className="font-semibold text-gray-800 border-b pb-2">Payment Details</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-500 mb-1">Amount</div>
              <div className="font-medium">
                {parseFloat(order.amount || 0).toFixed(2)} AFN
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-500 mb-1">Total Amount</div>
              <div className="font-bold text-primary-600">
                {parseFloat(order.totalAmount || order.amount || 0).toFixed(2)} AFN
              </div>
            </div>

            {order.source && (
              <div>
                <div className="text-sm text-gray-500 mb-1">Source</div>
                <div className="font-medium capitalize">
                  {order.source === 'agent_stock' ? 'Agent Wallet' : order.source}
                </div>
              </div>
            )}

            {order.promoCode && (
              <div>
                <div className="text-sm text-gray-500 mb-1">Promo Code</div>
                <div className="font-medium text-green-600">{order.promoCode}</div>
              </div>
            )}
          </div>

          {order.revenueValue > 0 && (
            <div className="mt-4 p-3 bg-primary-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Award size={16} className="text-primary-600" />
                <span className="text-sm font-medium text-primary-800">Revenue Details</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-primary-600">Revenue %:</span>
                  <span className="ml-2 font-medium">{order.revenueValue}%</span>
                </div>
                <div>
                  <span className="text-primary-600">Revenue Amount:</span>
                  <span className="ml-2 font-medium">{parseFloat(order.revenueAmount || 0).toFixed(2)} AFN</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {order.meta && Object.keys(order.meta).length > 0 && (
          <div className="space-y-4 mb-6">
            <h3 className="font-semibold text-gray-800 border-b pb-2">Additional Information</h3>
            <div className="bg-gray-50 p-3 rounded-lg max-h-[150px] overflow-y-auto">
              <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                {JSON.stringify(order.meta, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {order.status === 'pending' && (
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => onComplete(order.id)}
              disabled={isCompleting}
              className="flex-1 py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isCompleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle size={18} />
                  Complete
                </>
              )}
            </button>
            <button
              onClick={() => onReject(order)}
              disabled={isRejecting}
              className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isRejecting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <XCircle size={18} />
                  Reject
                </>
              )}
            </button>
          </div>
        )}

        {order.status !== 'pending' && (
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </StyledDialog>
  );
}